// src/pages/cartierCounterPage.jsx

import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import dayjs from "dayjs";
import HistorySpinner from "../components/historySpinner";

const CartierCounterPage = () => {
  const [modelNumber, setModelNumber] = useState("");
  const [caseNumber, setCaseNumber] = useState("");
  const [colorCode, setColorCode] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchEntries = async () => {
    try {
      const q = query(
        collection(db, "cartierCounterLogs"),
        orderBy("timestamp", "desc")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEntries(data);
    } catch (error) {
      console.error("Error fetching Cartier counter logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedModel = modelNumber.trim();
    const trimmedCase = caseNumber.trim();
    const trimmedColor = colorCode.trim();

    if (!trimmedModel || !trimmedCase) return;

    try {
      setSaving(true);

      const newEntry = {
        modelNumber: trimmedModel,
        caseNumber: trimmedCase,
        colorCode: trimmedColor || "N/A",
        user: auth.currentUser?.email || "Unknown",
        timestamp: serverTimestamp(),
      };

      await addDoc(collection(db, "cartierCounterLogs"), newEntry);

      setModelNumber("");
      setCaseNumber("");
      setColorCode("");

      await fetchEntries();
    } catch (error) {
      console.error("Error adding Cartier counter entry:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <HistorySpinner />;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Cartier Counter</h1>

      <form
        onSubmit={handleSubmit}
        className="bg-white border rounded-lg p-4 mb-6 shadow-sm"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Model Number</label>
            <input
              type="text"
              value={modelNumber}
              onChange={(e) => setModelNumber(e.target.value)}
              className="w-full border p-2 rounded"
              placeholder="Enter model number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Color Code</label>
            <input
              type="text"
              value={colorCode}
              onChange={(e) => setColorCode(e.target.value)}
              className="w-full border p-2 rounded"
              placeholder="Enter color code"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Case Number</label>
            <input
              type="text"
              value={caseNumber}
              onChange={(e) => setCaseNumber(e.target.value)}
              className="w-full border p-2 rounded"
              placeholder="Enter case number"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !modelNumber.trim() || !caseNumber.trim()}
          className="mt-4 px-4 py-2 bg-zinc-700 text-white rounded hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Add Entry"}
        </button>
      </form>

      <table className="w-full border text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-2 text-left">User</th>
            <th className="p-2 text-left">Model Number</th>
            <th className="p-2 text-left">Color Code</th>
            <th className="p-2 text-left">Case Number</th>
            <th className="p-2 text-right">Date</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-t">
              <td className="p-2">{entry.user || "Unknown"}</td>
              <td className="p-2">{entry.modelNumber || "Unknown"}</td>
              <td className="p-2">{entry.colorCode || "N/A"}</td>
              <td className="p-2">{entry.caseNumber || "Unknown"}</td>
              <td className="p-2 text-right">
                {entry.timestamp
                  ? dayjs(entry.timestamp.toDate()).format("YYYY-MM-DD HH:mm")
                  : "No Date"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CartierCounterPage;